import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react'
import type { AuthSession, SessionState } from '@concours/api-client'

const AuthContext = createContext<AuthSession | null>(null)

/**
 * Fournit la session du portail (créée une fois par l'application) et tente
 * de la restaurer au chargement via le cookie de rafraîchissement (ADR-020).
 */
export function AuthProvider({ session, children }: { session: AuthSession; children: ReactNode }) {
  useEffect(() => {
    void session.restore()
  }, [session])
  return <AuthContext.Provider value={session}>{children}</AuthContext.Provider>
}

export function useAuthSession(): AuthSession {
  const session = useContext(AuthContext)
  if (!session) throw new Error('useAuth doit être utilisé dans <AuthProvider>.')
  return session
}

/** État réactif de la session : { status: 'loading' | 'authenticated' | 'anonymous', user }. */
export function useAuth(): SessionState & { session: AuthSession } {
  const session = useAuthSession()
  const state = useSyncExternalStore(session.subscribe, session.getState, session.getState)
  return { ...state, session }
}

export interface RequireAuthProps {
  children: ReactNode
  /** Rendu quand l'utilisateur n'est pas connecté (ex. <Navigate to="/connexion" />). */
  fallback: ReactNode
  loading?: ReactNode
}

/** Garde de route : affiche `children` seulement pour une session ouverte. */
export function RequireAuth({ children, fallback, loading }: RequireAuthProps) {
  const { status } = useAuth()
  if (status === 'loading') return <>{loading ?? <FullPageSpinner />}</>
  if (status === 'anonymous') return <>{fallback}</>
  return <>{children}</>
}

/** Inverse : pages de connexion / inscription, inutiles une fois connecté. */
export function RedirectIfAuthenticated({ children, to }: { children: ReactNode; to: ReactNode }) {
  const { status } = useAuth()
  if (status === 'loading') return <FullPageSpinner />
  if (status === 'authenticated') return <>{to}</>
  return <>{children}</>
}

export function FullPageSpinner() {
  return (
    <div
      style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', color: 'var(--ink-3)' }}
      role="status"
    >
      <span className="spinner" style={{ width: 24, height: 24 }} aria-hidden="true" />
      <span className="sr-only">Chargement…</span>
    </div>
  )
}
